import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, PlusCircle, MapPin, Calendar, Search, ChevronDown, ChevronUp, Zap, Trash2, FileText, Filter } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import { Report, ReportType, ProjectDetails, ProjectWorkflowStatus } from '../../types';
import { EmptyState } from '../../components/common/EmptyState';
import { Badge } from '../../components/ui/Badge';
import { API_BASE_URL } from '../../config';

// Helper function for status badges
const getStatusBadge = (status?: ProjectWorkflowStatus, t?: (key: string) => string) => {
    switch (status) {
        case ProjectWorkflowStatus.PendingTeamAcceptance:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">{t ? t('projectStatusPendingTeamAcceptance') : 'بانتظار موافقة الفريق'}</span>;
        case ProjectWorkflowStatus.InProgress:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500 border border-blue-500/20">{t ? t('projectStatusInProgress') : 'قيد التنفيذ'}</span>;
        case ProjectWorkflowStatus.FinishingWorks:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">{t ? t('projectStatusFinishingWorks') : 'أعمال التشطيبات'}</span>;
        case ProjectWorkflowStatus.ConcreteWorksDone:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-500 border border-orange-500/20">{t ? t('projectStatusAwaitingSecondPayment') : 'بانتظار الدفعة الثانية'}</span>;
        case ProjectWorkflowStatus.TechnicallyCompleted:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">{t ? t('projectStatusTechnicallyCompleted') : 'مكتمل فنياً'}</span>;
        case ProjectWorkflowStatus.Finalized:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">{t ? t('projectStatusFinalized') : 'مكتمل'}</span>;
        default:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-500 border border-slate-500/20">{t ? t('projectStatusDraft') : 'مسودة'}</span>;
    }
}

const getStageLabelById = (id: string, t: (key: string) => string) => {
    switch (id) {
        case 'contract': return t('projectStageContract');
        case 'firstPayment': return t('projectStageFirstPayment');
        case 'notifyTeam': return t('projectStageNotifyTeam');
        case 'concreteWorks': return t('projectStageConcreteWorks');
        case 'secondPayment': return t('projectStageSecondPayment');
        case 'installationComplete': return t('projectStageInstallationComplete');
        case 'deliveryHandover': return t('projectStageDeliveryHandover');
        default: return id;
    }
};

const getCurrentStageLabel = (details: ProjectDetails, t: (key: string) => string): string => {
    const reversedUpdates = [...details.updates].reverse();
    const lastCompletedUpdate = reversedUpdates.find(u => u.completed);
    if (lastCompletedUpdate) {
        return `${t('projectCurrentStagePrefix')} ${getStageLabelById(lastCompletedUpdate.id, t)}`;
    }
    return t('projectStagesNotStarted');
};

const PROJECT_STAGES = [
    { id: 'contract', name: 'العقد' },
    { id: 'firstPayment', name: 'الدفعة الأولى' },
    { id: 'notifyTeam', name: 'إشعار الفريق' },
    { id: 'concreteWorks', name: 'الأعمال الخرسانية' },
    { id: 'secondPayment', name: 'الدفعة الثانية' },
    { id: 'installationComplete', name: 'التركيب' },
    { id: 'deliveryHandover', name: 'التسليم' },
];

const ModernProjectCard: React.FC<{ project: Report; index: number }> = ({ project, index }) => {
    const navigate = useNavigate();
    const { t } = useAppContext();
    const details = project.details as ProjectDetails;

    // Calculate current stage index
    const reversedUpdates = [...details.updates].reverse();
    const lastCompletedUpdate = reversedUpdates.find(u => u.completed);
    const currentStageIndex = lastCompletedUpdate
        ? PROJECT_STAGES.findIndex(s => s.id === lastCompletedUpdate.id)
        : -1;

    // Calculate percentage
    const progressPercentage = currentStageIndex >= 0
        ? Math.round(((currentStageIndex + 1) / PROJECT_STAGES.length) * 100)
        : 0;

    return (
        <div
            onClick={() => navigate(`/reports/${project.id}`)}
            className="group relative glass-card p-5 rounded-2xl hover:bg-slate-800/50 transition-all duration-300 cursor-pointer border border-slate-700/50 hover:border-blue-500/30 overflow-hidden animate-fade-in-up"
            style={{ animationDelay: `${index * 50}ms` }}
        >
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="flex justify-between items-start mb-4 pl-2">
                <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                        <Briefcase size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-lg leading-tight mb-1 group-hover:text-blue-400 transition-colors">{details.projectOwner}</h3>
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                            <span>{project.id}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                            <span>{new Date(details.startDate).toLocaleDateString('ar-SA')}</span>
                        </div>
                    </div>
                </div>
                {getStatusBadge(project.projectWorkflowStatus, t)}
            </div>

            <div className="space-y-4 mb-4">
                <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-slate-400">المرحلة الحالية</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-blue-400">
                                {currentStageIndex >= 0 ? PROJECT_STAGES[currentStageIndex].name : t('projectStagesNotStarted')}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono">
                                {progressPercentage}%
                            </span>
                        </div>
                    </div>

                    {/* 7-Stage Progress Indicator */}
                    <div className="flex gap-1.5 mt-2" dir="ltr">
                        {PROJECT_STAGES.map((stage, idx) => {
                            const isCompleted = currentStageIndex >= idx;
                            const isCurrent = currentStageIndex === idx;

                            let bgClass = 'bg-slate-700';
                            if (isCompleted) bgClass = 'bg-blue-500';
                            if (isCurrent) bgClass = 'bg-blue-400 animate-pulse';

                            return (
                                <div
                                    key={stage.id}
                                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${bgClass}`}
                                    title={stage.name}
                                />
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-800/30 p-2 rounded-lg border border-slate-700/30">
                    <MapPin size={12} className="text-slate-500" />
                    <span className="truncate">{details.location}</span>
                </div>
            </div>
        </div>
    );
};

const ModernProjectDashboardScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports } = useAppStore();
    const navigate = useNavigate();

    interface SavedQuotationSummary {
        id: string;
        quote_number: string;
        quote_date: string;
        customer_name: string;
        location: string | null;
        mobile: string | null;
        created_at: string;
        total_with_tax: number;
    }
    const [savedQuotations, setSavedQuotations] = useState<SavedQuotationSummary[]>([]);

    useEffect(() => {
        const fetchSaved = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/quotations?limit=12`);
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data)) setSavedQuotations(data as SavedQuotationSummary[]);
                else if (Array.isArray((data as any).items)) setSavedQuotations((data as any).items as SavedQuotationSummary[]);
            } catch { }
        };
        fetchSaved();
    }, []);

    const deleteQuotationCard = async (id: string) => {
        try {
            const ok = confirm('هل تريد حذف العرض؟');
            if (!ok) return;
            const res = await fetch(`${API_BASE_URL}/quotations/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(await res.text());
            setSavedQuotations(prev => prev.filter(q => q.id !== id));
        } catch (e) {
            alert('تعذر حذف العرض');
        }
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | ProjectWorkflowStatus>('all');

    const projectReports = useMemo(() => {
        if (!user) return [];
        return reports
            .filter(r => r.type === ReportType.Project && r.employeeId === user.employeeId)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [reports, user]);

    const filteredReports = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();
        return projectReports.filter(report => {
            const details = report.details as ProjectDetails;
            const searchMatch = searchLower === '' ||
                (report.employeeName || '').toLowerCase().includes(searchLower) ||
                (details.projectOwner || '').toLowerCase().includes(searchLower) ||
                (report.id || '').toLowerCase().includes(searchLower);
            const statusMatch = statusFilter === 'all' || report.projectWorkflowStatus === statusFilter;
            return searchMatch && statusMatch;
        });
    }, [projectReports, searchTerm, statusFilter]);

    const [projectsExpanded, setProjectsExpanded] = useState(true);
    const [quotationsExpanded, setQuotationsExpanded] = useState(false);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [quotationDetails, setQuotationDetails] = useState<Record<string, { horsepower?: number; device_type?: string; device_count?: number }>>({});

    useEffect(() => {
        if (!quotationsExpanded || savedQuotations.length === 0) return;
        const missing = savedQuotations.filter(q => !quotationDetails[q.id]);
        if (missing.length === 0) return;
        (async () => {
            try {
                const results = await Promise.all(missing.map(async (q) => {
                    const res = await fetch(`${API_BASE_URL}/quotations/${q.id}`);
                    if (!res.ok) return { id: q.id, items: [] } as any;
                    const data = await res.json();
                    const first = Array.isArray(data.items) && data.items.length > 0 ? data.items[0] : null;
                    return { id: q.id, first } as { id: string, first: any };
                }));
                setQuotationDetails(prev => {
                    const next = { ...prev };
                    for (const r of results) {
                        if (r.first) {
                            next[r.id] = {
                                horsepower: Number(r.first.horsepower || 0),
                                device_type: r.first.device_type,
                                device_count: r.first.device_count,
                            };
                        } else {
                            next[r.id] = {};
                        }
                    }
                    return next;
                });
            } catch { }
        })();
    }, [quotationsExpanded, savedQuotations]);

    return (
        <div className="space-y-8 animate-content-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">{t('projectReports')}</h1>
                    <p className="text-slate-400">إدارة ومتابعة مشاريعك وعروض الأسعار</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setFiltersCollapsed(v => !v)}
                        className={`p-3 rounded-xl transition-all ${!filtersCollapsed ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                    >
                        <Filter size={20} />
                    </button>
                    <button
                        onClick={() => navigate('/quotations/new')}
                        className="hidden sm:flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors font-medium border border-slate-700"
                    >
                        <FileText size={18} />
                        <span>{t('createQuotation')}</span>
                    </button>
                    <button
                        onClick={() => navigate('/projects/new')}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 transition-all font-medium"
                    >
                        <PlusCircle size={18} />
                        <span>{t('newProjectReport')}</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${filtersCollapsed ? 'max-h-0 opacity-0' : 'max-h-40 opacity-100'}`}>
                <div className="glass-panel p-4 rounded-2xl flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="بحث باسم الموظف, مالك المشروع, أو رقم التقرير..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-3 pr-11 pl-4 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                        />
                    </div>
                    <div className="md:w-64">
                        <div className="relative">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value as any)}
                                className="w-full appearance-none bg-slate-900/50 border border-slate-700 rounded-xl py-3 pr-4 pl-10 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            >
                                <option value="all">كل الحالات</option>
                                {Object.entries(ProjectWorkflowStatus).map(([key, value]) => (
                                    <option key={key} value={value}>{value}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Projects Section */}
            <div className="space-y-4">
                <div
                    onClick={() => setProjectsExpanded(v => !v)}
                    className="flex items-center justify-between cursor-pointer group select-none"
                >
                    <h2 className="text-xl font-bold text-white flex items-center gap-3">
                        <Briefcase className="text-blue-400" size={24} />
                        تقارير المشاريع
                        <span className="px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-400 text-sm font-medium border border-slate-700">{filteredReports.length}</span>
                    </h2>
                    <div className={`p-2 rounded-lg bg-slate-800/50 text-slate-400 group-hover:text-white transition-all ${projectsExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown size={20} />
                    </div>
                </div>

                <div className={`transition-all duration-500 ease-in-out ${projectsExpanded ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    {filteredReports.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredReports.map((report, idx) => (
                                <ModernProjectCard key={report.id} project={report} index={idx} />
                            ))}
                        </div>
                    ) : (
                        <div className="glass-panel p-12 rounded-2xl text-center">
                            <div className="w-16 h-16 mx-auto bg-slate-800/50 rounded-full flex items-center justify-center mb-4">
                                <Briefcase size={32} className="text-slate-600" />
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">{t('noProjectReports')}</h3>
                            <p className="text-slate-400 max-w-md mx-auto">{t('noProjectReportsMessage')}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Quotations Section */}
            <div className="space-y-4">
                <div
                    onClick={() => setQuotationsExpanded(v => !v)}
                    className="flex items-center justify-between cursor-pointer group select-none pt-4 border-t border-slate-800"
                >
                    <h2 className="text-xl font-bold text-white flex items-center gap-3">
                        <FileText className="text-emerald-400" size={24} />
                        عروض السعر
                        <span className="px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-400 text-sm font-medium border border-slate-700">{savedQuotations.length}</span>
                    </h2>
                    <div className={`p-2 rounded-lg bg-slate-800/50 text-slate-400 group-hover:text-white transition-all ${quotationsExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown size={20} />
                    </div>
                </div>

                <div className={`transition-all duration-500 ease-in-out ${quotationsExpanded ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    {savedQuotations.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {savedQuotations.map((item, idx) => (
                                <div
                                    key={item.id}
                                    onClick={() => navigate(`/quotations/${item.id}`)}
                                    className="glass-card p-5 rounded-2xl cursor-pointer group hover:-translate-y-1 transition-all duration-300 relative"
                                    style={{ animationDelay: `${idx * 50}ms` }}
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="font-bold text-white text-lg leading-tight mb-1">{item.customer_name}</h3>
                                            <p className="text-xs text-slate-400">{item.location || '-'}</p>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteQuotationCard(item.id); }}
                                            className="p-2 rounded-lg text-slate-500 hover:bg-red-500/10 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                            title="حذف العرض"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    <div className="space-y-2.5 mb-4">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-500 flex items-center gap-2"><MapPin size={14} /> موقع العميل</span>
                                            <span className="text-slate-300 truncate max-w-[55%]">{item.location || '-'}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-500 flex items-center gap-2"><Zap size={14} /> عدد الأحصنة</span>
                                            <span className="text-slate-300 font-mono">{quotationDetails[item.id]?.horsepower ?? '-'}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-500 flex items-center gap-2"><Calendar size={14} /> التاريخ</span>
                                            <span className="text-slate-300 font-mono">{new Date(item.quote_date).toLocaleDateString('en-GB')}</span>
                                        </div>
                                    </div>

                                    <div className="pt-3 border-t border-slate-700/50 flex justify-between items-center">
                                        <span className="text-xs text-slate-500">الإجمالي</span>
                                        <span className="font-bold text-emerald-400 font-mono">{Number(item.total_with_tax || 0).toLocaleString('en-GB')} ر.س</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="glass-panel p-8 rounded-2xl text-center">
                            <p className="text-slate-400">لا توجد عروض سعر محفوظة بعد.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModernProjectDashboardScreen;
